import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Footer } from "@/components/invoice-layout";
import { DemoBadge } from "@/components/demo";
import { formatCurrency } from "@/lib/format";

/**
 * The demo's bank authorisation screen ("Demo Bank"), where Pay by Bank sends a demo payer instead of a real bank. Approve
 * or Decline goes to the API's own bank callback, carrying the decision as a bank's redirect would; the callback then
 * sends the payer back to the page they came from. Only the signed state comes in the URL, never a link to follow.
 */
export function DemoBankPage() {
  const [params] = useSearchParams();
  const state = params.get("state");
  const payee = params.get("payee");
  const amount = Number(params.get("amount"));
  const currency = params.get("currency") ?? "GBP";
  const reference = params.get("reference");
  const [leaving, setLeaving] = useState<"approve" | "decline" | null>(null);

  useEffect(() => {
    document.title = "Demo Bank";
  }, []);

  function decide(decision: "approve" | "decline") {
    if (!state || leaving) return;
    setLeaving(decision);
    const callback = `${process.env.API_BASE_URL}/bank-payments/callback/${encodeURIComponent(state)}`;
    window.location.assign(
      decision === "approve"
        ? `${callback}?consent=demo-approved`
        : `${callback}?error=access_denied&error-description=${encodeURIComponent("The payment was declined at Demo Bank.")}`,
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4 sm:p-6 dark:bg-slate-950">
      <div className="w-full max-w-md">
        {!state || !Number.isFinite(amount) || amount <= 0 ? (
          <Card>
            <CardHeader className="items-center gap-3 text-center">
              <AlertTriangle className="h-10 w-10 text-muted-foreground" />
              <CardTitle>This payment request is incomplete</CardTitle>
              <CardDescription>Go back and start Pay by Bank again.</CardDescription>
            </CardHeader>
            <CardContent />
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between bg-slate-900 px-6 py-4 text-white">
              <span className="flex items-center gap-2 text-base font-semibold tracking-tight">
                <Landmark className="size-5" />
                Demo Bank
              </span>
              <DemoBadge className="border-amber-300/50 bg-amber-300/15 text-amber-200 dark:text-amber-200" />
            </div>
            <CardHeader className="gap-1">
              <CardDescription>Approve this payment from your Business Current Account</CardDescription>
              <CardTitle className="text-3xl tabular-nums">{formatCurrency(amount, currency)}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                <dt className="text-muted-foreground">To</dt>
                <dd className="font-medium">{payee || "The supplier"}</dd>
                {reference ? (
                  <>
                    <dt className="text-muted-foreground">Reference</dt>
                    <dd className="font-medium">{reference}</dd>
                  </>
                ) : null}
                <dt className="text-muted-foreground">From</dt>
                <dd className="font-medium">Business Current Account</dd>
              </dl>
              <p className="text-xs text-muted-foreground">
                A made-up bank for this demo. Approving pays the bill in the demo; no real money moves.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" disabled={leaving !== null} onClick={() => decide("decline")}>
                  {leaving === "decline" ? <Spinner className="h-4 w-4" /> : "Decline"}
                </Button>
                <Button
                  disabled={leaving !== null}
                  onClick={() => decide("approve")}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  {leaving === "approve" ? <Spinner className="h-4 w-4" /> : "Approve"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <Footer note={null} />
      </div>
    </div>
  );
}
